import {useState} from 'react'
import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  OnboardingControls,
  OnboardingDescriptionText,
  OnboardingPosition,
  OnboardingTitleText,
} from '#/screens/Onboarding/Layout'
import {useOnboardingInternalState} from '#/screens/Onboarding/state'
import {
  ORG_TYPE_ICONS,
  ORG_TYPES,
  type OrgTypeCategory,
} from '#/screens/Onboarding/StepGoal/data'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as TextField from '#/components/forms/TextField'
import * as Toggle from '#/components/forms/Toggle'
import {Text} from '#/components/Typography'

const CATEGORIES: OrgTypeCategory[] = [
  'Educational Institutions',
  'Training & Tutoring',
  'Knowledge & Culture',
  'Publishing & Media',
  'Corporate & Professional',
  'Other',
]

export function StepOrgType() {
  const {i18n, t: l} = useLingui()
  const t = useTheme()
  const {state, dispatch} = useOnboardingInternalState()

  const [orgTypeId, setOrgTypeId] = useState<string | null>(
    state.goalStepResults.orgType,
  )
  const [orgName, setOrgName] = useState<string>(
    state.goalStepResults.orgName ?? '',
  )

  const selectedOrg = ORG_TYPES.find(o => o.id === orgTypeId) ?? null

  function onContinue() {
    if (!orgTypeId) return
    dispatch({
      type: 'setGoalResults',
      results: {
        orgType: orgTypeId,
        orgName: orgTypeId === 'other_org' ? orgName : '',
      },
    })
    dispatch({type: 'next'})
  }

  return (
    <View style={[a.align_start, a.gap_sm]} testID="onboardingOrgType">
      <OnboardingPosition />
      <OnboardingTitleText>
        <Trans>Your Organisation Type 🏢</Trans>
      </OnboardingTitleText>
      <OnboardingDescriptionText>
        <Trans>
          Pick what best describes your institution — your profile and creator
          tools will be built around it.
        </Trans>
      </OnboardingDescriptionText>

      <View style={[a.w_full, a.pt_lg, a.gap_lg]}>
        {CATEGORIES.map(cat => {
          const items = ORG_TYPES.filter(o => o.category === cat)
          return (
            <View key={cat} style={[a.gap_sm]}>
              <Text
                style={[
                  a.text_xs,
                  {
                    fontFamily: 'monospace',
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                  },
                  t.atoms.text_contrast_low,
                ]}>
                <Trans>{cat}</Trans>
              </Text>
              <Toggle.Group
                values={orgTypeId ? [orgTypeId] : []}
                onChange={vals => {
                  const next = vals[vals.length - 1] ?? null
                  setOrgTypeId(next)
                  if (next !== 'other_org') setOrgName('')
                }}
                label={l`${cat}`}>
                <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
                  {items.map(org => (
                    <Toggle.Item
                      key={org.id}
                      name={org.id}
                      label={i18n._(org.label)}>
                      {({selected}) => (
                        <View
                          style={[
                            a.px_md,
                            a.py_sm,
                            a.rounded_full,
                            a.border,
                            selected
                              ? {
                                  borderColor: 'transparent',
                                  ...t.atoms.bg_contrast_975,
                                }
                              : t.atoms.border_contrast_low,
                          ]}>
                          <Text
                            style={[
                              a.text_sm,
                              a.font_bold,
                              selected && t.atoms.text_inverted,
                            ]}
                            emoji>
                            {ORG_TYPE_ICONS[org.id]} {i18n._(org.label)}
                          </Text>
                        </View>
                      )}
                    </Toggle.Item>
                  ))}
                </View>
              </Toggle.Group>
            </View>
          )
        })}
      </View>

      {selectedOrg && (
        <View
          style={[
            a.w_full,
            a.mt_md,
            a.p_md,
            a.border,
            {
              borderColor: t.atoms.border_contrast_low.borderColor,
              backgroundColor: t.atoms.bg_contrast_25.backgroundColor,
              borderRadius: 18,
            },
          ]}>
          <Text style={[a.text_sm, a.font_bold]} emoji>
            {ORG_TYPE_ICONS[selectedOrg.id]} {i18n._(selectedOrg.label)}
          </Text>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {i18n._(selectedOrg.desc)}
          </Text>
        </View>
      )}

      {orgTypeId === 'other_org' && (
        <TextField.Root style={[a.w_full]}>
          <TextField.Input
            label={l`Describe your organisation (optional)`}
            placeholder={l`e.g. Community learning hub, Artisan training workshop, EdTech startup…`}
            value={orgName}
            onChangeText={setOrgName}
            autoCapitalize="sentences"
          />
        </TextField.Root>
      )}

      <OnboardingControls.Portal>
        <Button
          disabled={!orgTypeId}
          testID="onboardingContinue"
          color="primary"
          size="large"
          label={l`Continue to next step`}
          onPress={onContinue}>
          <ButtonText>
            <Trans>Continue</Trans>
          </ButtonText>
        </Button>
      </OnboardingControls.Portal>
    </View>
  )
}
